import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { BsExclamationTriangleFill, BsInfoCircleFill } from 'react-icons/bs';
import { confirmedConstraintIssues } from './targetComparison';

const SHOW_AT_MOST = 8;
/** ~10 lines of 13px text plus the heading row; anything longer scrolls inside the banner. */
const BANNER_MAX_HEIGHT = 240;

const qualify = (schema, table) => (schema ? `${schema}.${table}` : table);

/** Capped list with a "Show all" toggle — same pattern as InventoryIssuesBanner. */
function CappedList({ items, render, testId }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, SHOW_AT_MOST);
  return (
    <>
      <ul className='mb-0 pl-4' style={{ fontSize: 13 }}>
        {shown.map((i, idx) => (
          <li key={idx} data-testid={`${testId}-item`}>
            {render(i)}
          </li>
        ))}
      </ul>
      {items.length > SHOW_AT_MOST && (
        <button
          type='button'
          className='btn btn-link p-0 mt-1'
          style={{ fontSize: 13 }}
          onClick={() => setExpanded((v) => !v)}
          data-testid={`${testId}-show-all`}
        >
          {expanded ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </>
  );
}

/**
 * Banner under the inventory title describing how the SELECTED mask target compares with the
 * inventory (server TargetComparisonReport). Sibling of InventoryIssuesBanner (which stays the
 * source-constraint banner). One of:
 *   - profile pending  → info, spinner: the target is being profiled; the compare re-runs after
 *   - profile failed   → danger: the Profile ended FAILED / CANCELLED / INTERRUPTED (UI-only
 *                        `profileFailed` set by the page's poll); `onRetry` re-compares, which
 *                        makes the server start a fresh Profile
 *   - no snapshot      → warning: run a Profile on the target first
 *   - nothing to mask  → danger: every inventory table is missing there
 *   - gaps             → warning: labeled inventory name + missing tables / columns lists
 *   - target constraint conflicts → danger list of CONFIRMED issues (COMPOSITE as a softer note)
 *   - clean            → nothing rendered (the target select's own hint covers the happy path)
 */
export default function TargetComparisonBanner({ report, targetName, derivedInventoryName, onRetry }) {
  if (!report || report.skipped) return null;
  const name = targetName || report.targetName || 'the target';
  const missingTables = Array.isArray(report.missingTables) ? report.missingTables : [];
  const missingColumns = Array.isArray(report.missingColumns) ? report.missingColumns : [];
  const confirmed = confirmedConstraintIssues(report);
  const composite = (Array.isArray(report.constraintIssues) ? report.constraintIssues : []).filter(
    (i) => i && String(i.level).toUpperCase() !== 'CONFIRMED'
  );
  const asOf = report.snapshotTakenAt ? new Date(report.snapshotTakenAt).toLocaleString() : null;

  if (report.profilePending) {
    return (
      <div className='alert alert-info mb-2 text-left' role='status' data-testid='target-comparison-banner' data-state='pending'>
        <div className='d-flex align-items-center'>
          <Spinner animation='border' size='sm' className='mr-2 flex-shrink-0' />
          <span>
            <strong>Fingerprinting {name}</strong> (job {report.profileJobId}) so its schema can be compared with this
            inventory — the comparison runs automatically when it completes.
          </span>
        </div>
      </div>
    );
  }
  if (report.profileFailed) {
    const status = String(report.profileStatus || 'FAILED').toLowerCase();
    const reason = String(report.profileError || '').trim().replace(/\.+$/, '');
    return (
      <div className='alert alert-danger mb-2 text-left' role='alert' data-testid='target-comparison-banner' data-state='profile-failed'>
        <div className='d-flex align-items-center'>
          <BsExclamationTriangleFill className='mr-2 flex-shrink-0' />
          <span>
            <strong>
              Fingerprint of {name} {status === 'cancelled' ? 'was cancelled' : status === 'interrupted' ? 'was interrupted' : 'failed'}
            </strong>
            {report.profileJobId != null ? ` (job ${report.profileJobId})` : ''}
            {reason ? ` — ${reason}` : ''}. Its schema could not be compared with this
            inventory.
            {onRetry && (
              <>
                {' '}
                <Button
                  variant='link'
                  size='sm'
                  className='p-0 align-baseline'
                  onClick={onRetry}
                  data-testid='target-comparison-retry'
                >
                  Fingerprint again
                </Button>
              </>
            )}
          </span>
        </div>
      </div>
    );
  }
  if (report.snapshotMissing) {
    return (
      <div className='alert alert-warning mb-2 text-left' role='alert' data-testid='target-comparison-banner' data-state='missing'>
        <div className='d-flex align-items-center'>
          <BsExclamationTriangleFill className='mr-2 flex-shrink-0' />
          <span>
            <strong>{name} has no fingerprinted schema.</strong> Run a Fingerprint on it before masking so the inventory can
            be checked against its tables and columns.
          </span>
        </div>
      </div>
    );
  }
  if (report.nothingToMask) {
    return (
      <div className='alert alert-danger mb-2 text-left' role='alert' data-testid='target-comparison-banner' data-state='nothing'>
        <div className='d-flex align-items-center'>
          <BsExclamationTriangleFill className='mr-2 flex-shrink-0' />
          <span>
            <strong>Nothing to mask on {name}</strong> — every table this inventory masks is missing there.
          </span>
        </div>
      </div>
    );
  }
  if (!report.hasGaps && confirmed.length === 0 && composite.length === 0) return null;

  // The gap / conflict lists can run long (and "Show all" lifts the per-list cap): cap the banner
  // at roughly ten lines of text and scroll the rest, so the inventory grid stays in view. The
  // page's flex column would otherwise squeeze it to a sliver — flexShrink 0 keeps it at its own
  // height up to the cap.
  return (
    <div
      className={`alert ${confirmed.length > 0 ? 'alert-danger' : 'alert-warning'} mb-2 text-left`}
      role='alert'
      data-testid='target-comparison-banner'
      data-state={report.hasGaps ? 'gaps' : 'constraints'}
      style={{ maxHeight: BANNER_MAX_HEIGHT, overflowY: 'auto', flexShrink: 0 }}
    >
      {report.hasGaps && (
        <div className='mb-1' data-testid='target-comparison-gaps'>
          <div className='d-flex align-items-center mb-1'>
            <BsExclamationTriangleFill className='mr-2 flex-shrink-0' />
            <strong data-testid='target-comparison-summary'>
              {derivedInventoryName ? (
                <>Inventory name: <code>{derivedInventoryName}</code></>
              ) : (
                `Not on ${name}`
              )}
            </strong>
          </div>
          <div className='mb-1' data-testid='target-comparison-gaps-note'>
            Chosen target is missing the following database elements that are in the inventory. They will be
            removed from the inventory.
          </div>
          {missingTables.length > 0 && (
            <>
              <div className='sg-small' style={{ fontWeight: 500 }}>Missing tables</div>
              <CappedList
                items={missingTables}
                testId='target-missing-table'
                render={(m) => <code>{qualify(m.schema, m.table)}</code>}
              />
            </>
          )}
          {missingColumns.length > 0 && (
            <>
              <div className='sg-small mt-1' style={{ fontWeight: 500 }}>Missing columns</div>
              <CappedList
                items={missingColumns}
                testId='target-missing-column'
                render={(m) => (
                  <>
                    <code>{qualify(m.schema, m.table)}.{m.column}</code>
                    {m.algorithm ? ` — ${m.algorithm}` : ''}
                  </>
                )}
              />
            </>
          )}
        </div>
      )}
      {confirmed.length > 0 && (
        <div className='mt-1' data-testid='target-comparison-constraints'>
          <div className='d-flex align-items-center mb-1'>
            <BsExclamationTriangleFill className='mr-2 flex-shrink-0' />
            <strong>
              {confirmed.length === 1
                ? `1 column will fail masking on ${name} with a duplicate-key error`
                : `${confirmed.length} columns will fail masking on ${name} with duplicate-key errors`}
            </strong>
          </div>
          <CappedList
            items={confirmed}
            testId='target-constraint-issue'
            render={(i) => (
              <>
                <code>{qualify(i.schema, i.table)}.{i.column}</code> — {i.algorithm}
              </>
            )}
          />
        </div>
      )}
      {composite.length > 0 && (
        <div className='mt-1 sg-small' data-testid='target-comparison-composite'>
          <BsInfoCircleFill className='mr-1' />
          {composite.length === 1 ? '1 column' : `${composite.length} columns`} on {name} sit under a composite,
          partial, or unenforced key constraint — duplicates are possible but not certain.
        </div>
      )}
      {asOf && (
        <div className='sg-small mt-1' style={{ color: 'var(--ink-500)' }} data-testid='target-comparison-asof'>
          Target schema as fingerprinted {asOf}.
        </div>
      )}
    </div>
  );
}
